import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  SupportRadioIcon,
  SupportTvIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CurrentUsers } from '@/components/CurrentUsers/CurrentUsers';
import { RenderEnrichementsIcons } from '@/components/RenderEnrichementsIcons/RenderEnrichementsIcons';
import { StartTimeEndTimePart } from '@/components/StartTimeEndTimePart/StartTimeEndTimePart';
import { StatusSeenChipInTable } from '@/components/StatusSeenChipInTable';
import { CLIP_REVIEW_STATES } from '@/constants';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { TClip } from '@/features/clips/types';
import { TProgram } from '@/features/programs/types';
import { ClipSyncStatusEnum } from '@/features/segments/types';
import { wrapperIconByStatus } from '@/helpers/wrapperIconStatus';
import { useClipPresence } from '@/providers/websocket';
import { getProgramBroadcastHours } from '@/utils/date';

type TClipMiniCardBaseCardHeaderProps = {
  item: TClip;
  program?: TProgram;
};

export function ClipMiniCardBaseCardHeader({
  item,
  program: selectedProgram,
}: TClipMiniCardBaseCardHeaderProps) {
  const userConnectedInClips = useClipPresence();

  const programDetails = useMemo(() => {
    if (!selectedProgram) return '';

    // A clip carries at most one emission, so there is nothing to rank. Name and window
    // both come from it, as they do for segments, so a clip and the segment it was cut
    // from read the same. An absent emission leaves the window empty rather than falling
    // back to a slot of the template, which would invent hours the clip has none of.
    //
    // The channel is not repeated here: the row below already names it.
    const name = item.program_broadcast?.name ?? selectedProgram.name;
    const hours = getProgramBroadcastHours(item.program_broadcast);
    return [name, hours].filter(Boolean).join(' · ');
  }, [selectedProgram, item.program_broadcast]);

  const { t } = useTranslation();
  const syncStatusMapper: Record<
    ClipSyncStatusEnum,
    {
      text: string;
      className?: string;
    }
  > = {
    [ClipSyncStatusEnum.FAILED]: {
      text: t('Sincronización fallida'),
      className: 'bg-feedback-error-20',
    },
    [ClipSyncStatusEnum.COMPLETED]: {
      text: t('Sincronización completa'),
      className: 'bg-extra-70',
    },
    [ClipSyncStatusEnum.WAITING_ENRICHMENTS]: {
      text: t('Esperando enriquecimientos'),
      className: 'bg-extra-80 animate-fade-in-out duration-1000',
    },
  };

  const { text: statusText, className: statusClassName } = syncStatusMapper[item.sync_status];

  return (
    <div className="flex w-full flex-row flex-wrap items-center gap-2 pr-2">
      <div
        className="ml-0.5 flex w-full items-center justify-between gap-2"
        data-id="program-details"
      >
        <Typography
          fontSize={FontSizeEnum.Sm}
          color={FontColorEnum.Dark10}
          weight={FontWeightEnum.Semibold}
        >
          {programDetails}
        </Typography>
        <div className="flex justify-end gap-2 md:flex-1">
          <RenderEnrichementsIcons
            enrichments={{
              ocr: item.parameters?.enrichments?.ocr,
              stt: item.parameters?.enrichments?.stt,
              logos: item.parameters?.enrichments?.logos,
              peoples: item.parameters?.enrichments?.peoples,
            }}
            statusEnrichments={{
              ocr: item.parameters?.status_enrichments?.ocr?.status,
              stt: item.parameters?.status_enrichments?.stt?.status,
              logos: item.parameters?.status_enrichments?.logos?.status,
              peoples: item.parameters?.status_enrichments?.peoples?.status,
            }}
            classname="items-center"
          />
          {userConnectedInClips[item.id] && <CurrentUsers ids={userConnectedInClips[item.id]} />}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Tooltip help={statusText} grow>
          {wrapperIconByStatus(
            item.media_type.includes(ChannelMediaTypeEnum.TV) ? (
              <SupportTvIcon />
            ) : (
              <SupportRadioIcon />
            ),
            clsx(statusClassName, '!-right-0.5 !h-2 !w-2')
          )}
        </Tooltip>

        <StatusSeenChipInTable
          item={item}
          review_state={item.review_state}
          reviewStates={CLIP_REVIEW_STATES}
        />

        <Typography
          color={FontColorEnum.Dark10}
          fontSize={FontSizeEnum.Body3}
          weight={FontWeightEnum.Bold}
        >
          {selectedProgram?.channel.name}
        </Typography>
        <StartTimeEndTimePart
          startDateTime={item?.start_datetime}
          endDateTime={item.end_datetime}
        />
      </div>
    </div>
  );
}
